import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import { getStringFromTO } from '../common/utils/i18nUtils';
import { convertToAO1 } from '../common/utils/assetUtils';

const DefaultImage = 'https://dummyimage.com/100x150/e9ecef/6c757d&text=No+Image';

const ProjectContainer = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: start;
`;

const GameImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const GameBoxArt = styled.img`
  width: 100px;
  height: 150px; /* 2:3 aspect ratio (same as GameTile: 145×210) - proper board game box art ratio */
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const GameImageLabel = styled.span`
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  text-align: center;
`;

const ProjectInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProjectTitle = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  color: #212529;
  font-weight: 600;
`;

const ProjectMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  background: white;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetaLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  color: #495057;
  font-weight: 500;
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const IdBadge = styled.span`
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8rem;
  color: #6c757d;
  background: #f8f9fa;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
`;

const ProjectImagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ProjectImage = styled.img`
  width: 140px;
  height: 105px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #dee2e6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ImageLabel = styled.span`
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  text-align: center;
`;

const GameImageSection = ({ game }) => {
  console.log('🖼️ GameImageSection - Received game:', game);
  console.log('🖼️ GameImageSection - game.assets:', game.assets);
  console.log('🖼️ GameImageSection - game.assets?.boxart:', game.assets?.boxart);
  
  const gameBoxArt = game.assets?.boxart && convertToAO1(game.assets.boxart);
  console.log('🖼️ GameImageSection - convertToAO1 result:', gameBoxArt);
  
  return (
    <GameImageContainer>
      <GameBoxArt 
        src={gameBoxArt?.url || DefaultImage} 
        alt="Game Box Art" 
      />
      <GameImageLabel>Game Box Art</GameImageLabel>
    </GameImageContainer>
  );
};

const ProjectInfoSection = ({ project, game }) => {
  // Helper function to get friendly project type name
  const getProjectTypeName = (type) => {
    switch (type) {
      case 'tct':
        return 'Tutorial';
      case 'rules':
        return 'Rules';
      case 'extra':
        return 'Extra';
      default:
        return type; // Fallback to original type if unknown
    }
  };

  const baseProjectTitle = getStringFromTO(project.title) || 'Unknown Project';
  const friendlyProjectType = getProjectTypeName(project.type);
  
  // Add project type prefix
  let projectTitle;
  if (project.type === 'tct') {
    projectTitle = `Tutorial: ${baseProjectTitle}`;
  } else if (project.type === 'rules') {
    projectTitle = `Rules: ${baseProjectTitle}`;
  } else if (project.type === 'extra') {
    projectTitle = `Extra: ${baseProjectTitle}`;
  } else {
    // Fallback for any other types
    projectTitle = baseProjectTitle;
  }
  
  console.log('🔍 ProjectDescription - Raw game object:', game);
  console.log('🔍 ProjectDescription - Raw game.title:', game.title);
  // Since getNormalizedGame() now returns a plain string for title, handle both cases
  const gameTitle = (typeof game.title === 'string' ? game.title : getStringFromTO(game.title)) || 'Unknown Game';
  console.log('🔍 ProjectDescription - Processed gameTitle:', gameTitle);
  const projectUuid = project.uuid || 'N/A';
  const gameUuid = game.uuid || 'N/A';

  return (
    <ProjectInfo>
      <ProjectTitle>{projectTitle}</ProjectTitle>
      <ProjectMetaGrid>
        <MetaItem>
          <MetaLabel>Project Type</MetaLabel>
          <MetaValue>
            <TypeBadge>{friendlyProjectType}</TypeBadge>
          </MetaValue>
        </MetaItem>
        
        <MetaItem>
          <MetaLabel>Project ID</MetaLabel>
          <MetaValue>
            <IdBadge>{projectUuid}</IdBadge>
          </MetaValue>
        </MetaItem>
        
        <MetaItem>
          <MetaLabel>Game</MetaLabel>
          <MetaValue>{gameTitle}</MetaValue>
        </MetaItem>
        
        <MetaItem>
          <MetaLabel>Game ID</MetaLabel>
          <MetaValue>
            <IdBadge>{gameUuid}</IdBadge>
          </MetaValue>
        </MetaItem>
        
        {project.description?.text && (
          <MetaItem style={{ gridColumn: '1 / -1' }}>
            <MetaLabel>Description</MetaLabel>
            <MetaValue>{project.description.text}</MetaValue>
          </MetaItem>
        )}
      </ProjectMetaGrid>
    </ProjectInfo>
  );
};

const ProjectImagesSection = ({ project }) => {
  console.log('🖼️ ProjectImagesSection - Received project:', project);
  console.log('🖼️ ProjectImagesSection - project.projectImage:', project.projectImage);
  console.log('🖼️ ProjectImagesSection - project.backgroundImage:', project.backgroundImage);
  
  // Convert project images using convertToAO1 to handle both legacy and modern formats
  const projectImage = project.projectImage && convertToAO1(project.projectImage);
  const backgroundImage = project.backgroundImage && convertToAO1(project.backgroundImage);
  
  console.log('🖼️ ProjectImagesSection - convertToAO1 projectImage result:', projectImage);
  console.log('🖼️ ProjectImagesSection - convertToAO1 backgroundImage result:', backgroundImage);
  
  return (
    <ProjectImagesContainer>
      {project.projectImage && (
        <ImageContainer>
          <ProjectImage src={projectImage?.url || DefaultImage} alt="Project Menu Image" />
          <ImageLabel>Menu Image</ImageLabel>
        </ImageContainer>
      )}
      
      {project.backgroundImage && (
        <ImageContainer>
          <ProjectImage src={backgroundImage?.url || DefaultImage} alt="Project Background Image" />
          <ImageLabel>Background Image</ImageLabel>
        </ImageContainer>
      )}
    </ProjectImagesContainer>
  );
};

export const ProjectDescription = ({ project, game }) => {
  if (!project || !game) {
    return (
      <Section>
        <ProjectTitle>Project Information</ProjectTitle>
        <p>No project information available</p>
      </Section>
    );
  }

  return (
    <Section>
      <ProjectContainer>
        <HeaderContent>
          <GameImageSection game={game} />
          <ProjectInfoSection project={project} game={game} />
          <ProjectImagesSection project={project} />
        </HeaderContent>
      </ProjectContainer>
    </Section>
  );
};

// Add PropTypes for sub-components
GameImageSection.propTypes = {
  game: PropTypes.shape({
    assets: PropTypes.shape({
      boxart: PropTypes.object
    })
  }).isRequired
};

ProjectInfoSection.propTypes = {
  project: PropTypes.shape({
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        text: PropTypes.string
      })
    ]),
    type: PropTypes.string,
    uuid: PropTypes.string,
    description: PropTypes.shape({
      text: PropTypes.string
    })
  }).isRequired,
  game: PropTypes.shape({
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        text: PropTypes.string
      })
    ]),
    uuid: PropTypes.string
  }).isRequired
};

ProjectImagesSection.propTypes = {
  project: PropTypes.shape({
    projectImage: PropTypes.shape({
      url: PropTypes.string
    }),
    backgroundImage: PropTypes.shape({
      url: PropTypes.string
    })
  }).isRequired
};

ProjectDescription.propTypes = {
  project: PropTypes.shape({
    uuid: PropTypes.string,
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        text: PropTypes.string
      })
    ]),
    type: PropTypes.string,
    description: PropTypes.shape({
      text: PropTypes.string
    }),
    projectImage: PropTypes.shape({
      url: PropTypes.string
    }),
    backgroundImage: PropTypes.shape({
      url: PropTypes.string
    })
  }),
  game: PropTypes.shape({
    uuid: PropTypes.string,
    title: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        text: PropTypes.string
      })
    ]),
    assets: PropTypes.shape({
      boxart: PropTypes.object
    })
  })
};

ProjectDescription.displayName = 'ProjectDescription';
